import { type Rng } from '@/core/rng';
import { type HeroTemplateId } from '@/data/heroes';
import { type WorldBounds, type WorldCollider } from '@/world/zone';
import { createCharacter, type CharacterState } from './character';
import { shortestAngleDelta, slideAgainstTrees } from './movement';

const NPC_SPEED_WANDER = 2.5;
const NPC_SPEED_PATROL = 2.0;
const NPC_TURN_RATE = 6;
const ARRIVAL_DIST = 0.4;
const PAUSE_MIN = 0.6;
const PAUSE_MAX = 2.5;
const PATROL_PAUSE = 0.4;
const NPC_RADIUS = 0.3;

export interface PatrolWaypoint {
  x: number;
  z: number;
}

export interface Npc extends CharacterState {
  templateId: HeroTemplateId;
  rng: Rng;
  targetX: number;
  targetZ: number;
  pauseRemaining: number;
  recruited: boolean;
  /** Defeated NPCs are hidden + un-collidable until their respawn timer
   *  hits zero. respawn() resets position + state. */
  defeated: boolean;
  /** Seconds remaining before respawn (only meaningful when defeated). */
  respawnIn: number;
  /** Optional fixed patrol path. When set, the NPC loops through these
   *  waypoints instead of picking random wander targets. */
  patrol: ReadonlyArray<PatrolWaypoint> | null;
  patrolIdx: number;
}

const pickWanderTarget = (npc: Npc, bounds: WorldBounds): void => {
  const margin = 0.5;
  npc.targetX = npc.rng.range(bounds.minX + margin, bounds.maxX - margin);
  npc.targetZ = npc.rng.range(bounds.minZ + margin, bounds.maxZ - margin);
};

const advancePatrol = (npc: Npc): void => {
  if (npc.patrol === null || npc.patrol.length === 0) return;
  npc.patrolIdx = (npc.patrolIdx + 1) % npc.patrol.length;
  const wp = npc.patrol[npc.patrolIdx]!;
  npc.targetX = wp.x;
  npc.targetZ = wp.z;
};

export interface CreateNpcOptions {
  patrol?: ReadonlyArray<PatrolWaypoint>;
  startAt?: { x: number; z: number };
}

export const createNpc = (
  templateId: HeroTemplateId,
  rng: Rng,
  bounds: WorldBounds,
  options: CreateNpcOptions = {},
): Npc => {
  const x =
    options.startAt?.x ??
    options.patrol?.[0]?.x ??
    rng.range(bounds.minX + 1.5, bounds.maxX - 1.5);
  const z =
    options.startAt?.z ??
    options.patrol?.[0]?.z ??
    rng.range(bounds.minZ + 1.5, bounds.maxZ - 1.5);
  const yaw = rng.range(0, Math.PI * 2);
  const patrol = options.patrol ?? null;
  return {
    ...createCharacter(x, z, yaw),
    templateId,
    rng,
    targetX: patrol !== null && patrol.length > 0 ? patrol[0]!.x : x,
    targetZ: patrol !== null && patrol.length > 0 ? patrol[0]!.z : z,
    pauseRemaining: rng.range(PAUSE_MIN, PAUSE_MAX),
    recruited: false,
    defeated: false,
    respawnIn: 0,
    patrol,
    patrolIdx: 0,
  };
};

/**
 * Reset a defeated NPC to a fresh wandering / patrolling state. Position
 * picker tries up to 16 random spots to avoid landing inside a collider;
 * patrol NPCs respawn at their first waypoint instead.
 */
export const respawnNpc = (
  npc: Npc,
  bounds: WorldBounds,
  trees: ReadonlyArray<WorldCollider>,
): void => {
  npc.defeated = false;
  npc.respawnIn = 0;
  if (npc.patrol !== null && npc.patrol.length > 0) {
    npc.x = npc.patrol[0]!.x;
    npc.z = npc.patrol[0]!.z;
    npc.patrolIdx = 0;
    npc.targetX = npc.x;
    npc.targetZ = npc.z;
  } else {
    const margin = 1.5;
    let placed = false;
    for (let attempt = 0; attempt < 16; attempt++) {
      const x = npc.rng.range(bounds.minX + margin, bounds.maxX - margin);
      const z = npc.rng.range(bounds.minZ + margin, bounds.maxZ - margin);
      let ok = true;
      for (const t of trees) {
        const dx = x - t.x;
        const dz = z - t.z;
        const min = t.radius + NPC_RADIUS + 0.2;
        if (dx * dx + dz * dz < min * min) {
          ok = false;
          break;
        }
      }
      if (ok) {
        npc.x = x;
        npc.z = z;
        npc.targetX = x;
        npc.targetZ = z;
        placed = true;
        break;
      }
    }
    if (!placed) {
      // Give up gracefully — the NPC will collide-out on the next tick.
      npc.x = npc.rng.range(bounds.minX + margin, bounds.maxX - margin);
      npc.z = npc.rng.range(bounds.minZ + margin, bounds.maxZ - margin);
      npc.targetX = npc.x;
      npc.targetZ = npc.z;
    }
  }
  npc.pauseRemaining = npc.rng.range(PAUSE_MIN, PAUSE_MAX);
  npc.animTime = 0;
  npc.yaw = npc.rng.range(0, Math.PI * 2);
};

export const updateNpc = (
  npc: Npc,
  dtSec: number,
  trees: ReadonlyArray<WorldCollider>,
  bounds: WorldBounds,
): boolean => {
  npc.animTime += dtSec;

  if (npc.pauseRemaining > 0) {
    npc.pauseRemaining -= dtSec;
    if (npc.pauseRemaining <= 0) {
      if (npc.patrol !== null) advancePatrol(npc);
      else pickWanderTarget(npc, bounds);
    }
    return false;
  }

  const dx = npc.targetX - npc.x;
  const dz = npc.targetZ - npc.z;
  const dist = Math.hypot(dx, dz);
  if (dist < ARRIVAL_DIST) {
    npc.pauseRemaining =
      npc.patrol !== null ? PATROL_PAUSE : npc.rng.range(PAUSE_MIN, PAUSE_MAX);
    return false;
  }

  const inv = 1 / dist;
  const vx = dx * inv;
  const vz = dz * inv;
  const speed = npc.patrol !== null ? NPC_SPEED_PATROL : NPC_SPEED_WANDER;

  let nx = npc.x + vx * speed * dtSec;
  let nz = npc.z + vz * speed * dtSec;

  if (nx < bounds.minX + NPC_RADIUS) nx = bounds.minX + NPC_RADIUS;
  else if (nx > bounds.maxX - NPC_RADIUS) nx = bounds.maxX - NPC_RADIUS;
  if (nz < bounds.minZ + NPC_RADIUS) nz = bounds.minZ + NPC_RADIUS;
  else if (nz > bounds.maxZ - NPC_RADIUS) nz = bounds.maxZ - NPC_RADIUS;

  const [cx, cz] = slideAgainstTrees(trees, npc.x, npc.z, nx, nz, NPC_RADIUS);
  npc.x = cx;
  npc.z = cz;

  const targetYaw = Math.atan2(vx, -vz);
  const dyaw = shortestAngleDelta(targetYaw, npc.yaw);
  const maxStep = NPC_TURN_RATE * dtSec;
  npc.yaw += Math.abs(dyaw) > maxStep ? Math.sign(dyaw) * maxStep : dyaw;

  return true;
};
